import { useMutation } from '@tanstack/react-query'
import { useCallback } from 'react'
import { useAuthenticatedUser } from '@omnea-app/fe-auth/UserSessionProvider'
import { throwIfApiError } from '@omnea-app/packages/universal/error-handling/throwifApiError'
import { RequestsHttpClient } from '@omnea-app/requests/requestsHttpClient'

export type UpdateRequestConfigParams =
  | { target?: 'config'; requestId: string; blockId: string; updateRequest: boolean; updateRequestStep: boolean }
  | { target: 'conditionConfig'; requestId: string; blockId: string }

export type UpdateRequestConfigResult = {
  validationSuccessful: boolean
  requestUpdated: boolean
  requestStepUpdated: boolean
  outcome?: 'updated' | 'unchanged' | 'skipped_concurrent_update' | 'not_applied'
}

const callUpdateBlockConfigApi = async (token: string, params: UpdateRequestConfigParams): Promise<UpdateRequestConfigResult> => {
  const apiClient = new RequestsHttpClient({ authorization: token })
  const body =
    params.target === 'conditionConfig'
      ? { updateConditionConfig: true }
      : { updateRequest: params.updateRequest, updateRequestStep: params.updateRequestStep }
  const { data, error } = await apiClient.POST('/{id}/blocks/{blockId}/update-block-config', {
    params: { path: { id: params.requestId, blockId: params.blockId } },
    body,
  })
  throwIfApiError(error)
  if (!data) throw new Error('No data returned from update-block-config')
  return data
}

export const useUpdateRequestConfig = () => {
  const { getToken } = useAuthenticatedUser()

  return useMutation({
    mutationFn: async (params: UpdateRequestConfigParams) => {
      const token = await getToken()
      return callUpdateBlockConfigApi(token, params)
    },
  })
}

/**
 * Non-reactive variant: returns a stable caller without subscribing the component to mutation state.
 * Prefer this when firing many updates (e.g. bulk), so per-call state transitions don't re-render the caller.
 */
export const useUpdateRequestConfigFn = () => {
  const { getToken } = useAuthenticatedUser()

  return useCallback(
    async (params: UpdateRequestConfigParams) => {
      const token = await getToken()
      return callUpdateBlockConfigApi(token, params)
    },
    [getToken]
  )
}
